
import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../../lib/supabase'

interface Post {
  id: string
  title: string
  content: string
  upvotes: number
  downvotes: number
  comment_count: number
  created_at: string
  tags: string[]
}

interface Comment {
  id: string
  content: string
  upvotes: number
  downvotes: number
  created_at: string
  post_id: string
  post?: {
    id: string
    title: string
  }
}

interface ActivityItem {
  id: string
  type: 'post' | 'comment'
  title?: string
  content: string
  upvotes: number
  downvotes: number
  comment_count?: number
  created_at: string
  post_id?: string
  post_title?: string
  tags?: string[]
}

interface ProfileActivityHistoryProps {
  userId: string
}

const ProfileActivityHistory = ({ userId }: ProfileActivityHistoryProps) => {
  const [activeFilter, setActiveFilter] = useState<'all' | 'posts' | 'comments'>('all')
  const [posts, setPosts] = useState<Post[]>([])
  const [comments, setComments] = useState<Comment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const fetchActivity = async () => {
      if (!userId) return
      
      setLoading(true)
      setError('')

      try {
        const [postsResult, commentsResult] = await Promise.all([
          supabase
            .from('posts')
            .select('id, title, content, upvotes, downvotes, comment_count, created_at, tags')
            .eq('author_id', userId)
            .eq('status', 'approved')
            .order('created_at', { ascending: false })
            .limit(20),
          supabase
            .from('comments')
            .select('id, content, upvotes, downvotes, created_at, post_id')
            .eq('author_id', userId)
            .eq('status', 'approved')
            .order('created_at', { ascending: false })
            .limit(20)
        ])

        if (postsResult.error) throw postsResult.error
        if (commentsResult.error) throw commentsResult.error

        setPosts(postsResult.data || [])

        // Fetch post titles for comments
        const commentsWithPosts = commentsResult.data || []
        if (commentsWithPosts.length > 0) {
          const postIds = [...new Set(commentsWithPosts.map(c => c.post_id))]
          const { data: postTitles } = await supabase
            .from('posts')
            .select('id, title')
            .in('id', postIds)

          const postTitleMap = new Map(postTitles?.map(p => [p.id, p.title]) || [])
          
          setComments(commentsWithPosts.map(comment => ({
            ...comment,
            post: {
              id: comment.post_id,
              title: postTitleMap.get(comment.post_id) || 'Unknown Post'
            }
          })))
        } else {
          setComments([])
        }
      } catch (err: any) {
        console.error('Error fetching activity:', err)
        setError('Failed to load activity history')
      } finally {
        setLoading(false)
      }
    }

    fetchActivity()
  }, [userId])

  const getFilteredActivity = (): ActivityItem[] => {
    let items: ActivityItem[] = []

    if (activeFilter === 'all' || activeFilter === 'posts') {
      items = [
        ...items,
        ...posts.map(post => ({
          id: post.id,
          type: 'post' as const,
          title: post.title,
          content: post.content,
          upvotes: post.upvotes,
          downvotes: post.downvotes,
          comment_count: post.comment_count,
          created_at: post.created_at,
          tags: post.tags
        }))
      ]
    }

    if (activeFilter === 'all' || activeFilter === 'comments') {
      items = [
        ...items,
        ...comments.map(comment => ({
          id: comment.id,
          type: 'comment' as const,
          content: comment.content,
          upvotes: comment.upvotes,
          downvotes: comment.downvotes,
          created_at: comment.created_at,
          post_id: comment.post_id,
          post_title: comment.post?.title
        }))
      ]
    }

    return items.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
  }

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

    if (diffInSeconds < 60) return 'Just now'
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  const truncateText = (text: string, maxLength: number) => {
    if (!text) return ''
    const plainText = text.replace(/<[^>]*>/g, '')
    if (plainText.length <= maxLength) return plainText
    return plainText.substring(0, maxLength) + '...'
  }

  const filteredActivity = getFilteredActivity()

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="animate-pulse">
            <div className="bg-gray-100 rounded-xl p-4">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
                <div className="h-4 bg-gray-200 rounded w-24"></div>
              </div>
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <i className="ri-error-warning-line text-red-500 text-2xl mb-2"></i>
        <p className="text-red-700">{error}</p>
        <button 
          onClick={() => window.location.reload()}
          className="mt-3 text-sm text-red-600 hover:text-red-700 font-medium cursor-pointer"
        >
          Try Again
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Filter Tabs */}
      <div className="flex items-center space-x-2 bg-gray-100 rounded-full p-1 w-fit">
        {[
          { id: 'all', label: 'All', count: posts.length + comments.length },
          { id: 'posts', label: 'Posts', count: posts.length },
          { id: 'comments', label: 'Comments', count: comments.length }
        ].map(filter => (
          <button
            key={filter.id}
            onClick={() => setActiveFilter(filter.id as any)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
              activeFilter === filter.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {filter.label}
            <span className={`ml-1.5 text-xs ${activeFilter === filter.id ? 'text-amber-600' : 'text-gray-400'}`}>
              {filter.count}
            </span>
          </button>
        ))}
      </div>

      {/* Activity List */}
      {filteredActivity.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
            <i className={`${activeFilter === 'posts' ? 'ri-article-line' : activeFilter === 'comments' ? 'ri-chat-3-line' : 'ri-history-line'} text-2xl text-gray-400`}></i>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">
            {activeFilter === 'posts' ? 'No Posts Yet' : activeFilter === 'comments' ? 'No Comments Yet' : 'No Activity Yet'}
          </h3>
          <p className="text-gray-500 text-sm">
            {activeFilter === 'posts' 
              ? 'Start sharing your thoughts with the community!' 
              : activeFilter === 'comments'
              ? 'Join discussions and share your opinions!'
              : 'Your posts and comments will appear here.'}
          </p>
          {activeFilter !== 'comments' && (
            <Link 
              to="/forum" 
              className="inline-flex items-center mt-4 text-amber-600 hover:text-amber-700 font-medium text-sm cursor-pointer"
            >
              <i className="ri-add-line mr-1"></i>
              Create Your First Post
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredActivity.map(item => (
            <Link
              key={`${item.type}-${item.id}`}
              to={item.type === 'post' ? `/forum/post/${item.id}` : `/forum/post/${item.post_id}`}
              className="block bg-gray-50 hover:bg-gray-100 rounded-xl p-4 transition-colors cursor-pointer group"
            >
              <div className="flex items-start space-x-3">
                {/* Type Icon */}
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  item.type === 'post' 
                    ? 'bg-amber-100 text-amber-600' 
                    : 'bg-emerald-100 text-emerald-600'
                }`}>
                  <i className={item.type === 'post' ? 'ri-article-line' : 'ri-chat-3-line'}></i>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      item.type === 'post'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-emerald-100 text-emerald-700'
                    }`}>
                      {item.type === 'post' ? 'Post' : 'Comment'}
                    </span>
                    <span className="text-xs text-gray-400">{formatTimeAgo(item.created_at)}</span>
                  </div>

                  {item.type === 'post' && item.title && (
                    <h4 className="font-medium text-gray-900 mb-1 group-hover:text-amber-600 transition-colors line-clamp-1">
                      {item.title}
                    </h4>
                  )}

                  {item.type === 'comment' && item.post_title && (
                    <p className="text-xs text-gray-500 mb-1">
                      <i className="ri-reply-line mr-1"></i>
                      Replied to: <span className="font-medium text-gray-700">{truncateText(item.post_title, 50)}</span>
                    </p>
                  )}

                  <p className="text-sm text-gray-600 line-clamp-2">
                    {truncateText(item.content, 150)}
                  </p>

                  {/* Tags for posts */}
                  {item.type === 'post' && item.tags && item.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {item.tags.slice(0, 3).map((tag, idx) => (
                        <span key={idx} className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                          #{tag}
                        </span>
                      ))}
                      {item.tags.length > 3 && (
                        <span className="text-xs text-gray-400">+{item.tags.length - 3}</span>
                      )}
                    </div>
                  )}

                  {/* Stats */}
                  <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                    <span className="flex items-center space-x-1">
                      <i className="ri-arrow-up-line text-emerald-500"></i>
                      <span>{item.upvotes || 0}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <i className="ri-arrow-down-line text-red-400"></i>
                      <span>{item.downvotes || 0}</span>
                    </span>
                    {item.type === 'post' && (
                      <span className="flex items-center space-x-1">
                        <i className="ri-chat-3-line text-gray-400"></i>
                        <span>{item.comment_count || 0} comments</span>
                      </span>
                    )}
                  </div>
                </div>

                {/* Arrow */}
                <i className="ri-arrow-right-s-line text-gray-400 group-hover:text-amber-500 transition-colors"></i>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

export default ProfileActivityHistory
